/**
 * Persian numerals, money and quantities.
 *
 * Every number the cashier sees passes through here. The font's FD cut maps
 * ASCII digits to Persian glyphs as a safety net, but that only changes what is
 * drawn — the underlying string stays Latin, so anything copied, exported or
 * sent to مودیان would carry the wrong characters. Converting for real is what
 * makes the value and its rendering agree.
 */

const DIGITS = '۰۱۲۳۴۵۶۷۸۹';

/** Latin digits to Persian, leaving everything else alone. */
export function fa(v: string | number): string {
  return String(v).replace(/[0-9]/g, (c) => DIGITS[Number(c)]);
}

/** Persian (and Arabic) digits back to Latin, for anything that must compute. */
export function toLatin(v: string): string {
  return v
    .replace(/[۰-۹]/g, (c) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(c)))
    .replace(/[٠-٩]/g, (c) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(c)));
}

/** Thousands separated with the Persian separator, not a Latin comma. */
export function group(n: number): string {
  const neg = n < 0;
  const s = String(Math.round(Math.abs(n))).replace(/\B(?=(\d{3})+(?!\d))/g, '٬');
  // U+2212 minus, not a hyphen: a hyphen next to Persian digits renders as a
  // dash of the wrong weight and can flip to the wrong side of the number.
  return (neg ? '−' : '') + fa(s);
}

/*
 * Money is stored in Rial and shown in Toman.
 *
 * A shopkeeper says «۹۵ هزار تومان» and the tax authority wants Rial, so the
 * unit is part of the value and never a separate label the caller might forget.
 * Passing a bare number around is how a price ends up ten times wrong in one
 * place and right everywhere else.
 */
export function toman(rial: number): string {
  return `${group(rial / 10)} تومان`;
}

/** The same figure without its unit, for when a layout supplies the unit. */
export function tomanBare(rial: number): string {
  return group(rial / 10);
}

/**
 * A quantity, which may be fractional for anything weighed.
 *
 * The decimal mark is «٫» (U+066B), not a full stop: ۱٫۵ is one and a half,
 * while ۱.۵ reads as two separate numbers to a Persian speaker.
 */
export function qty(n: number): string {
  if (Number.isInteger(n)) return fa(n);
  return fa(String(Number(n.toFixed(3))).replace('.', '٫'));
}
